import {
  getInvoiceTransformationLimitMessage,
  INVOICE_TRANSFORMATION_LIMITS,
  InvoiceTransformationAvailableField,
  InvoiceTransformationOutputModeEnum,
  InvoiceTransformationRequestUi,
  InvoiceTransformationTargetEnum,
} from '@monetize/types/app';
import { FC, useMemo, useState } from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import { IoMdTrash } from 'react-icons/io';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MFormField,
  MText,
  MTooltip,
} from '../Monetize';
import { MCustomMultiSelect } from '../Monetize/MCustomSelect/MCustomMultiSelect';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';
import { InvoiceTransformationAddButton } from './InvoiceTransformationAddButton';
import { InvoiceTransformationCondition } from './InvoiceTransformationCondition';
import { MultiValueInput } from './MultiValueInput';
import {
  getArrayErrorMessage,
  getNewCondition,
  getTargetOptions,
} from './invoiceTransformation.utils';

interface InvoiceTransformationConditionGroupProps {
  lineRuleIndex: number;
  groupIndex: number;
  availableFields: InvoiceTransformationAvailableField[];
  formContext: UseFormReturn<InvoiceTransformationRequestUi>;
  isReadOnly: boolean;
  canRemoveGroup: boolean;
  onRemoveGroup: () => void;
}

export const InvoiceTransformationConditionGroup: FC<
  InvoiceTransformationConditionGroupProps
> = ({
  lineRuleIndex,
  groupIndex,
  availableFields,
  formContext,
  isReadOnly,
  canRemoveGroup,
  onRemoveGroup,
}) => {
  const {
    control,
    formState: { errors, submitCount },
    getValues,
    setValue,
    watch,
  } = formContext;

  const groupPath =
    `invoiceLineRules.${lineRuleIndex}.conditionGroups.${groupIndex}` as const;
  const targetPath = `${groupPath}.target` as const;
  const conditionsPath = `${groupPath}.conditions` as const;
  const groupByPath = `${groupPath}.groupBy` as const;

  const target = watch(targetPath);
  const isUsageEvent = target === InvoiceTransformationTargetEnum.USAGE_EVENT;
  const watchedGroupBy = watch(groupByPath);

  // Group By is opt-in via a "+ Group By" button. Show the control once the
  // user adds it, or when it already has a saved value.
  const [showGroupBy, setShowGroupBy] = useState(
    (watchedGroupBy ?? []).length > 0,
  );

  const mode = watch('mode');
  const groupBySplitNoun =
    mode === InvoiceTransformationOutputModeEnum.LINE_TRANSFORMATION
      ? 'line'
      : 'invoice';
  const groupByTooltip = `Optional. Splits the matched lines into a separate ${groupBySplitNoun} for each unique combination of values in the chosen field(s).`;
  const targetOptions = getTargetOptions(mode);
  // When only one target is available (e.g. line transformations are
  // usage-event only) there's nothing to pick, so disable the input.
  const isTargetFixed = targetOptions.length <= 1;

  const { fields, append, remove } = useFieldArray({
    name: conditionsPath,
    control,
  });

  const groupErrors =
    errors.invoiceLineRules?.[lineRuleIndex]?.conditionGroups?.[groupIndex];

  // Collapse array errors
  const groupByError = groupErrors?.groupBy;
  const groupByFieldError = Array.isArray(groupByError)
    ? groupByError.find((entry) => entry?.message)
    : groupByError;

  const isGroupEmpty =
    fields.length === 0 && (watchedGroupBy ?? []).length === 0;
  // At the limit neither Group By input can take more (the chip input hides
  // its text box, the picker disables unchecked options), so say why. Skipped
  // when over the limit, where the validation error already explains it.
  const groupByHint =
    !isReadOnly &&
    !groupByFieldError &&
    (watchedGroupBy ?? []).length >= INVOICE_TRANSFORMATION_LIMITS.groupBy.max
      ? getInvoiceTransformationLimitMessage(
          INVOICE_TRANSFORMATION_LIMITS.groupBy,
        )
      : undefined;
  // The form validates as the user edits, so an emptied group would be flagged
  // straight away. Hold that error back until the user has tried to save.
  const conditionsError =
    isGroupEmpty && submitCount === 0
      ? undefined
      : getArrayErrorMessage(groupErrors?.conditions);

  const handleTargetChange = (next: InvoiceTransformationTargetEnum) => {
    const previous = getValues(targetPath);
    setValue(targetPath, next, { shouldDirty: true, shouldValidate: true });
    if (previous === next) {
      return;
    }
    // Field paths are target-specific (invoice-item paths vs. usage-event
    // custom property names), so clear them when the target changes.
    const conditions = getValues(conditionsPath) ?? [];
    conditions.forEach((_, conditionIndex) => {
      setValue(`${conditionsPath}.${conditionIndex}.field`, '', {
        shouldDirty: true,
      });
    });
    setValue(groupByPath, [], { shouldDirty: true });
  };

  const sortedAvailableFields = useMemo(() => {
    const byDescription = new Map<
      string,
      InvoiceTransformationAvailableField
    >();
    for (const field of availableFields) {
      const existing = byDescription.get(field.description);
      if (!existing || field.fieldPath.includes('.')) {
        byDescription.set(field.description, field);
      }
    }
    return [...byDescription.values()].sort((a, b) =>
      a.description.localeCompare(b.description),
    );
  }, [availableFields]);

  return (
    <MBox border="1px solid" borderColor="tGray.back" borderRadius="3px" p={3}>
      <MFlex justify="space-between" align="center" minH={8} mb={2} gap={2}>
        <MFlex align="center" gap={2}>
          <MBox minW="180px">
            <Controller
              name={targetPath}
              control={control}
              render={({ field: { onChange: _onChange, ...rest } }) => (
                <MCustomSelect
                  items={targetOptions}
                  itemTitle="title"
                  itemValue="value"
                  isDisabled={isTargetFixed || isReadOnly}
                  onChange={(next) =>
                    handleTargetChange(next as InvoiceTransformationTargetEnum)
                  }
                  {...rest}
                />
              )}
            />
          </MBox>
          <MText fontWeight="semibold" lineHeight={1}>
            where
          </MText>
        </MFlex>
        {!isReadOnly && canRemoveGroup && (
          <MCustomIconButton
            variant="icon"
            icon={IoMdTrash}
            onClick={onRemoveGroup}
            iconColor="tRed.base"
            aria-label="Remove condition set"
          />
        )}
      </MFlex>

      {fields.length > 0 &&
        fields.map((fieldItem, conditionIndex) => (
          <InvoiceTransformationCondition
            key={fieldItem.id}
            lineRuleIndex={lineRuleIndex}
            groupIndex={groupIndex}
            conditionIndex={conditionIndex}
            availableFields={sortedAvailableFields}
            isUsageEvent={isUsageEvent}
            formContext={formContext}
            isReadOnly={isReadOnly}
            canRemove
            showLabels={conditionIndex === 0}
            onRemove={() => remove(conditionIndex)}
          />
        ))}

      {!isReadOnly && (
        <MFlex mt={2} mb={3} align="center" gap={3}>
          <InvoiceTransformationAddButton
            label="+ Add Condition"
            count={fields.length}
            limit={INVOICE_TRANSFORMATION_LIMITS.conditions}
            tooltip="Optionally add conditions to limit which invoice lines this set targets."
            error={conditionsError}
            onClick={() => append(getNewCondition())}
          />
        </MFlex>
      )}

      {!showGroupBy ? (
        !isReadOnly && (
          <MTooltip label={groupByTooltip} placement="right">
            <MButton
              variant="tertiary"
              size="sm"
              onClick={() => setShowGroupBy(true)}
            >
              + Group By
            </MButton>
          </MTooltip>
        )
      ) : (
        <MFormField
          label="Group By"
          tooltip={groupByTooltip}
          error={groupByFieldError}
          hint={groupByHint}
        >
          <MFlex align="center" gap={2}>
            <MBox flex="1">
              <Controller
                name={groupByPath}
                control={control}
                render={({ field: { value, onChange, onBlur } }) => {
                  const selected = value || [];
                  if (isUsageEvent) {
                    return (
                      <MultiValueInput
                        value={selected}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder="Choose field(s) to group by"
                        isDisabled={isReadOnly}
                        maxValues={INVOICE_TRANSFORMATION_LIMITS.groupBy.max}
                      />
                    );
                  }
                  return (
                    <MCustomMultiSelect
                      placeholder="Choose field(s) to group by"
                      items={sortedAvailableFields}
                      itemTitle="description"
                      itemValue="fieldPath"
                      value={selected}
                      onChange={(next) => onChange(next)}
                      onBlur={onBlur}
                      isDisabled={isReadOnly}
                      closeButtonText="Apply"
                      selectionLimit={INVOICE_TRANSFORMATION_LIMITS.groupBy.max}
                      showCheckCount
                      showQueryInput
                    />
                  );
                }}
              />
            </MBox>
            {!isReadOnly && (
              <MCustomIconButton
                variant="icon"
                icon={IoMdTrash}
                onClick={() => {
                  setValue(groupByPath, [], {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setShowGroupBy(false);
                }}
                iconColor="tRed.base"
                aria-label="Remove group by"
              />
            )}
          </MFlex>
        </MFormField>
      )}
    </MBox>
  );
};
